import React from 'react';
import { Bell, BellRing, Bot, Check, Trash2, UserPlus, MessageSquare, Clock, AlertTriangle, Zap, Mail } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDateTime } from '../../contexts/DateTimeContext';
import BotAvatar from '../bots/BotAvatar';

export const TYPE_ICONS = {
  task_assigned: UserPlus,
  task_commented: MessageSquare,
  team_invite_accepted: UserPlus,
  task_reminder: BellRing,
  deadline_warning: Clock,
  overdue: AlertTriangle,
  auto_schedule_done: Zap,
  email_update: Mail,
  system: Bell,
  bot_update: Bot,
};

export const TYPE_COLORS = {
  task_assigned: 'text-blue-500',
  task_commented: 'text-purple-500',
  team_invite_accepted: 'text-green-500',
  task_reminder: 'text-amber-500',
  deadline_warning: 'text-yellow-500',
  overdue: 'text-red-500',
  auto_schedule_done: 'text-indigo-500',
  email_update: 'text-red-400',
  system: 'text-gray-500',
  bot_update: 'text-amber-500',
};

/** The bot a notification is from (its bee instead of the type's icon), from what its push carried. */
export const botOf = (n) => (n?.meta?.sender_id ? { id: n.meta.sender_id, avatar_color: n.meta.sender_color || null } : null);

/** Where a notification leads: the exact task or chat it is about. */
export function notificationUrl(notification) {
  // Notifications from before 2026-10-27 point at /app/tasks without the task: open it anyway.
  let url = notification?.action_url;
  if (notification?.related_entity_type === 'task' && notification.related_entity_id
    && !String(url || '').includes('task=')) {
    url = `/app/tasks?task=${notification.related_entity_id}`;
  }
  return url || null;
}

export function NotificationItem({ notification, onMarkRead, onDelete, onNavigate }) {
  const Icon = TYPE_ICONS[notification.type] || Bell;
  const colorClass = TYPE_COLORS[notification.type] || 'text-gray-500';
  const { t } = useTranslation();
  const { dateLocale } = useDateTime();

  const timeAgo = (() => {
    try {
      return formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: dateLocale });
    } catch {
      return '';
    }
  })();

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkRead(notification.id);
    }
    const url = notificationUrl(notification);
    if (url) {
      onNavigate(url);
    }
  };

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer hover:bg-white/5 transition-colors group ${
        !notification.is_read ? 'bg-white/[0.06]' : ''
      }`}
      onClick={handleClick}
    >
      <div className={`mt-0.5 shrink-0 ${colorClass}`}>
        {botOf(notification) ? <BotAvatar bot={botOf(notification)} size={18} /> : <Icon size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-tight ${!notification.is_read ? 'font-medium text-white' : 'font-normal text-gray-400'}`}>
          {notification.title}
        </p>
        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-gray-500 mt-1">{timeAgo}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {!notification.is_read && (
          <button
            className="p-1 rounded hover:bg-white/5 text-gray-400 hover:text-white"
            onClick={(e) => { e.stopPropagation(); onMarkRead(notification.id); }}
            title={t('notifications.mark_as_read')}
          >
            <Check size={12} />
          </button>
        )}
        <button
          className="p-1 rounded hover:bg-white/5 text-gray-400 hover:text-red-400"
          onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
          title={t('common.delete')}
        >
          <Trash2 size={12} />
        </button>
      </div>
      {!notification.is_read && (
        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0 mt-1.5" />
      )}
    </div>
  );
}
